import React, { useEffect, useRef, useState } from 'react';
import { getInvoices, createInvoice, updateInvoice, addInvoicePayment, createAvoir, uploadInvoicePdf, deleteInvoicePdf, getInvoicePdfUrl } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, FileMinus2, CreditCard, Clock, Paperclip, FileText, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Kpi, fmtEuro } from './shared';

const TYPE_LABELS = {
    acompte: { label: 'Acompte', color: 'bg-blue-100 text-blue-700' },
    situation: { label: 'Situation', color: 'bg-purple-100 text-purple-700' },
    solde: { label: 'Solde', color: 'bg-green-100 text-green-700' },
    avoir: { label: 'Avoir', color: 'bg-red-100 text-red-700' }
};

const STATUS_LABELS = {
    emise: 'Émise',
    partiel: 'Paiement partiel',
    payee: 'Payée',
    annulee: 'Annulée'
};

export const FacturationTab = ({ dossier, onChange }) => {
    const dossierId = dossier.id;
    const caDevis = Number(dossier.montant_devis || 0);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paymentInvoice, setPaymentInvoice] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [uploadingId, setUploadingId] = useState(null);
    const pdfInputRefs = useRef({});
    const [form, setForm] = useState({
        numero_facture: '',
        type_facture: 'situation',
        date_facture: format(new Date(), 'yyyy-MM-dd'),
        montant: '',
        description: '',
        reglement_differe: false,
        date_paiement_prevu: ''
    });

    const load = async () => {
        setLoading(true);
        try {
            const res = await getInvoices(dossierId);
            setInvoices(res.data || []);
        } catch {
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [dossierId]);
    const totalFacture = invoices.filter((i) => i.type_facture !== 'avoir').reduce((s, i) => s + Number(i.montant || 0), 0);
    const totalAvoirs = invoices.filter((i) => i.type_facture === 'avoir').reduce((s, i) => s + Math.abs(Number(i.montant || 0)), 0);
    const totalPaye = invoices.reduce((s, i) => s + Number(i.montant_paye || 0), 0);
    const factureNet = totalFacture - totalAvoirs;
    const progress = caDevis > 0 ? Math.min((factureNet / caDevis) * 100, 100) : 0;
    const restant = Math.max(caDevis - factureNet, 0);

    const handleAdd = async () => {
        if (!form.montant || !form.date_facture || !form.numero_facture) {
            toast.error('N° facture, montant et date requis');
            return;
        }
        try {
            const payload = {
                dossier_id: dossierId,
                numero_facture: form.numero_facture,
                type_facture: form.type_facture,
                date_facture: form.date_facture,
                montant: Number(form.montant),
                description: form.description || undefined,
                reglement_differe: !!form.reglement_differe,
                date_paiement_prevu: form.reglement_differe && form.date_paiement_prevu ? form.date_paiement_prevu : undefined
            };
            await createInvoice(payload);
            setForm({
                numero_facture: '',
                type_facture: 'situation',
                date_facture: format(new Date(), 'yyyy-MM-dd'),
                montant: '',
                description: '',
                reglement_differe: false,
                date_paiement_prevu: ''
            });
            await load();
            onChange && onChange();
            toast.success('Facture enregistrée');
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'ajout");
        }
    };

    const handlePayment = async () => {
        if (!paymentAmount || Number(paymentAmount) <= 0) {
            toast.error('Montant invalide');
            return;
        }
        try {
            await addInvoicePayment(paymentInvoice.id, { montant: Number(paymentAmount) });
            toast.success('Paiement enregistré');
            setPaymentInvoice(null);
            setPaymentAmount('');
            load();
            onChange && onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const handleAvoir = async (inv) => {
        if (!window.confirm(`Créer un avoir de ${fmtEuro(Math.abs(inv.montant))} € pour la facture ${inv.numero_facture} ?`)) return;
        try {
            await createAvoir(inv.id);
            toast.success('Avoir créé');
            load();
            onChange && onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const handlePdfPick = (invId) => {
        const ref = pdfInputRefs.current[invId];
        if (ref) ref.click();
    };

    const handlePdfUpload = async (inv, file) => {
        if (!file) return;
        if (file.size > 20 * 1024 * 1024) {
            toast.error('Fichier trop volumineux (max 20 Mo)');
            return;
        }
        setUploadingId(inv.id);
        try {
            await uploadInvoicePdf(inv.id, file);
            toast.success('PDF joint à la facture');
            await load();
            onChange && onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur upload');
        } finally {
            setUploadingId(null);
            if (pdfInputRefs.current[inv.id]) pdfInputRefs.current[inv.id].value = '';
        }
    };

    const handlePdfDelete = async (inv) => {
        if (!window.confirm(`Retirer le PDF de la facture ${inv.numero_facture} ?`)) return;
        try {
            await deleteInvoicePdf(inv.id);
            toast.success('PDF retiré');
            load();
            onChange && onChange();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    return (
        <div className="bg-white border border-[#E4E4E7] p-6 rounded-sm space-y-6" data-testid="facturation-tab">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Kpi label="CA devis HT" value={`${fmtEuro(caDevis)} €`} />
                <Kpi label="Facturé net HT" value={`${fmtEuro(factureNet)} €`} tone="ok" />
                <Kpi label="Encaissé HT" value={`${fmtEuro(totalPaye)} €`} tone="ok" />
                <Kpi label="Reste à facturer HT" value={`${fmtEuro(restant)} €`} />
            </div>
            {totalAvoirs > 0 && (
                <p className="text-xs text-[#52525B]">Total avoirs : {fmtEuro(totalAvoirs)} € HT</p>
            )}

            <div>
                <div className="flex justify-between items-center text-sm mb-1">
                    <span className="font-medium text-[#52525B]">Avancement facturation</span>
                    <span className="font-semibold text-[#0A0A0A]">{progress.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-[#F4F4F5] h-3 rounded-full overflow-hidden">
                    <div className="h-full bg-[#E85A24] transition-all" style={{ width: `${progress}%` }} />
                </div>
            </div>

            <div className="pt-4 border-t border-[#E4E4E7]">
                <h3 className="font-semibold mb-3">Ajouter une facture</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <Label className="field-label">N° facture *</Label>
                        <Input
                            value={form.numero_facture}
                            onChange={(e) => setForm({ ...form, numero_facture: e.target.value })}
                            placeholder="N° facture"
                            className="h-10 rounded-sm mt-1"
                            data-testid="invoice-numero-input"
                        />
                    </div>
                    <div>
                        <Label className="field-label">Type *</Label>
                        <Select value={form.type_facture} onValueChange={(v) => setForm({ ...form, type_facture: v })}>
                            <SelectTrigger className="h-10 rounded-sm mt-1" data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="acompte">Acompte</SelectItem>
                                <SelectItem value="situation">Situation</SelectItem>
                                <SelectItem value="solde">Solde</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="field-label">Date *</Label>
                        <Input
                            type="date"
                            value={form.date_facture}
                            onChange={(e) => setForm({ ...form, date_facture: e.target.value })}
                            className="h-10 rounded-sm mt-1 px-3"
                            data-testid="invoice-date-input"
                        />
                    </div>
                    <div>
                        <Label className="field-label">Montant € HT *</Label>
                        <Input
                            type="number"
                            step="0.01"
                            value={form.montant}
                            onChange={(e) => setForm({ ...form, montant: e.target.value })}
                            placeholder="Montant HT"
                            className="h-10 rounded-sm mt-1"
                            data-testid="invoice-montant-input"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label className="field-label">Description</Label>
                        <Input
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Description optionnelle"
                            className="h-10 rounded-sm mt-1"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.reglement_differe}
                            onChange={(e) => setForm({ ...form, reglement_differe: e.target.checked })}
                            className="h-4 w-4"
                            data-testid="invoice-reglement-differe"
                        />
                        Règlement différé (agence/syndic)
                    </label>
                    {form.reglement_differe && (
                        <Input
                            type="date"
                            value={form.date_paiement_prevu}
                            onChange={(e) => setForm({ ...form, date_paiement_prevu: e.target.value })}
                            className="h-9 rounded-sm w-48 px-3"
                            placeholder="Date prévisionnelle"
                        />
                    )}
                    <div className="flex-1" />
                    <Button
                        onClick={handleAdd}
                        className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm"
                        data-testid="add-invoice-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                    </Button>
                </div>
            </div>

            <div>
                <h3 className="font-semibold mb-3">Factures ({invoices.length})</h3>
                {loading ? (
                    <p className="text-sm text-[#52525B]">Chargement...</p>
                ) : invoices.length === 0 ? (
                    <p className="text-sm text-[#52525B] italic">Aucune facture</p>
                ) : (
                    <div className="divide-y divide-[#E4E4E7] border border-[#E4E4E7] rounded-sm">
                        {invoices.map((inv) => {
                            const typeBadge = TYPE_LABELS[inv.type_facture || 'situation'] || TYPE_LABELS.situation;
                            const restantInv = Math.abs(Number(inv.montant || 0)) - Number(inv.montant_paye || 0);
                            const isAvoir = inv.type_facture === 'avoir';
                            return (
                                <div key={inv.id} className="flex items-center justify-between p-3 text-sm gap-3" data-testid={`invoice-row-${inv.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-sm ${typeBadge.color}`}>
                                                {typeBadge.label}
                                            </span>
                                            <span className="font-medium">{inv.numero_facture || 'Sans n°'}</span>
                                            {inv.reglement_differe && (
                                                <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-sm flex items-center gap-1">
                                                    <Clock className="w-3 h-3" /> Différé
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-[#52525B] mt-0.5">
                                            {inv.date_facture ? format(new Date(inv.date_facture), 'dd/MM/yyyy', { locale: fr }) : '-'}
                                            {inv.description && ` · ${inv.description}`}
                                            {inv.date_paiement_prevu && ` · Prévu le ${format(new Date(inv.date_paiement_prevu), 'dd/MM/yyyy', { locale: fr })}`}
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={`font-semibold ${isAvoir ? 'text-red-600' : ''}`}>{fmtEuro(inv.montant)} €</p>
                                        {!isAvoir && Number(inv.montant_paye || 0) > 0 && (
                                            <p className="text-xs text-[#52525B]">
                                                Payé : {fmtEuro(inv.montant_paye)} €
                                                {restantInv > 0.01 && ` · Reste ${fmtEuro(restantInv)} €`}
                                            </p>
                                        )}
                                    </div>
                                    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-sm shrink-0 ${
                                        inv.statut === 'payee' ? 'bg-green-100 text-green-700' :
                                        inv.statut === 'partiel' ? 'bg-amber-100 text-amber-700' :
                                        'bg-gray-100 text-gray-700'
                                    }`}>
                                        {STATUS_LABELS[inv.statut] || 'Émise'}
                                    </span>
                                    {/* Hidden file input for PDF upload */}
                                    <input
                                        ref={(el) => { pdfInputRefs.current[inv.id] = el; }}
                                        type="file"
                                        accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
                                        className="hidden"
                                        onChange={(e) => handlePdfUpload(inv, e.target.files?.[0])}
                                        data-testid={`invoice-pdf-input-${inv.id}`}
                                    />
                                    {inv.pdf_path || inv.pdf_filename ? (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <a
                                                href={getInvoicePdfUrl(inv.id)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[#0284C7] hover:bg-[#0284C7]/10 px-2 py-1 rounded-sm text-xs flex items-center gap-1"
                                                title={inv.pdf_filename || 'Voir le PDF'}
                                                data-testid={`invoice-pdf-view-${inv.id}`}
                                            >
                                                <FileText className="w-4 h-4" /> Voir PDF
                                            </a>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => handlePdfDelete(inv)}
                                                className="h-8 w-8 text-[#52525B] hover:text-red-600"
                                                title="Retirer le PDF"
                                                data-testid={`invoice-pdf-delete-${inv.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handlePdfPick(inv.id)}
                                            disabled={uploadingId === inv.id}
                                            className="rounded-sm shrink-0"
                                            data-testid={`invoice-pdf-upload-${inv.id}`}
                                        >
                                            <Paperclip className="w-4 h-4 mr-1" />
                                            {uploadingId === inv.id ? '...' : 'Joindre PDF'}
                                        </Button>
                                    )}
                                    {!isAvoir && inv.statut !== 'payee' && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => { setPaymentInvoice(inv); setPaymentAmount(String(restantInv.toFixed(2))); }}
                                            className="rounded-sm shrink-0"
                                            data-testid={`pay-invoice-${inv.id}`}
                                        >
                                            <CreditCard className="w-4 h-4 mr-1" /> Paiement
                                        </Button>
                                    )}
                                    {!isAvoir && (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => handleAvoir(inv)}
                                            className="h-8 w-8 text-red-600 hover:bg-red-50 shrink-0"
                                            title="Créer un avoir"
                                            data-testid={`avoir-invoice-${inv.id}`}
                                        >
                                            <FileMinus2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Dialog open={!!paymentInvoice} onOpenChange={(o) => !o && setPaymentInvoice(null)}>
                <DialogContent data-testid="payment-dialog">
                    <DialogHeader>
                        <DialogTitle>Enregistrer un paiement</DialogTitle>
                    </DialogHeader>
                    {paymentInvoice && (
                        <div className="space-y-3">
                            <p className="text-sm text-[#52525B]">
                                Facture <b>{paymentInvoice.numero_facture}</b> de {fmtEuro(paymentInvoice.montant)} € HT.
                                {Number(paymentInvoice.montant_paye || 0) > 0 && (
                                    <> Déjà payé : {fmtEuro(paymentInvoice.montant_paye)} €.</>
                                )}
                            </p>
                            <div>
                                <Label className="field-label">Montant du paiement (€ HT)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentAmount}
                                    onChange={(e) => setPaymentAmount(e.target.value)}
                                    className="h-10 rounded-sm mt-1"
                                    data-testid="payment-amount-input"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPaymentInvoice(null)}>Annuler</Button>
                        <Button onClick={handlePayment} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white" data-testid="payment-confirm-btn">
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
